import { useCallback, useState } from "react";
import FileDownload from "js-file-download";
import { useNavigate } from "react-router-dom";
import { EDBService } from "$services";
import useAuth from "./useAuth";
import { queryClient } from "$utils/queryClient";
import logger from "$utils/logger";
import { CacheInvalidationAction } from "$utils/cache";
import { UseEDBType } from "$containers/EDB/ProfileEDB/types/useEDB.types";

const useEDB = (): UseEDBType => {
  const { token } = useAuth();
  const [step, setStep] = useState(0);
  const navigate = useNavigate();

  const [submiting, setSubmiting] = useState<boolean>(false);

  const [isModifyingDelivery, setIsModifyingDelivery] =
    useState<boolean>(false);

  const [errorFrom, setErrorForm] = useState("");

  const deleteEDB = useCallback(
    async (idEDB: string) => {
      await EDBService.DeleteEDB(token, idEDB);
    },
    [token]
  );

  const saveEDB = useCallback(
    async (data: any) => {
      if (!data._id) {
        try {
          const res = await EDBService.AddEDB(token, { ...data });
          setSubmiting(false);
          navigate(`/edbs?created=true&edbId=${res.data.edbId}`);
          setErrorForm("");
        } catch (e) {
          logger.error(e);
          setErrorForm(
            "Une erreur est intervenue lors de la création d'une EDB"
          );
          setSubmiting(false);
        }
      } else {
        const tmpData = { ...data };
        tmpData.updatedAt = Date.now();
        let edb;
        edb = await queryClient.getQueryData([
          CacheInvalidationAction.GET_EDB_BY_ID,
          data._id,
        ]);
        if (!edb) {
          edb = await queryClient.fetchQuery({
            queryKey: [CacheInvalidationAction.GET_EDB_BY_ID, data._id],
            queryFn: () => EDBService.getEdbById(data._id),
            staleTime: 5 * 60 * 1000,
          });
          await queryClient.setQueryData(
            [CacheInvalidationAction.GET_EDB_BY_ID, data._id],
            edb
          );
        }

        const tmpProducts = [];
        data.products.forEach((cartProduct) => {
          const itm = edb.products?.find(
            (edbProduct) =>
              edbProduct.relatedProduct._id === cartProduct.productID
          );
          tmpProducts.push({
            productID: cartProduct.productID,
            relatedProductId: itm ? itm._id : "",
            qty: cartProduct.qty,
          });
        });
        tmpData.products = tmpProducts;
        await EDBService.UpdateEDB(token, tmpData, tmpData._id)
          .then(async () => {
            navigate(`/edbs?updated=true&edbId=${edb.edbId}`);
            setSubmiting(false);
            setErrorForm("");
          })
          .catch(() => {
            setErrorForm(
              "Une erreur est intervenue lors de la création d'une EDB"
            );
            setSubmiting(false);
          });
      }
    },
    [navigate, token]
  );

  // TODO : les données ne sont pas rafraichie
  const updateEDB = useCallback(
    async (data: any, edbId: string) => {
      EDBService.UpdateEDB(token, data, edbId)
        .then(async () => {
          setSubmiting(false);
          setErrorForm("");
        })
        .catch(() => {
          setErrorForm(
            "Une erreur est intervenue lors de la création d'une EDB"
          );
          setSubmiting(false);
        });
    },
    [token]
  );

  const updateEDBProducts = useCallback(
    async (data: any, edb: any) => {
      const tmpProducts = [];
      data.products.forEach((cartProduct) => {
        const edbProduct = edb?.products?.find(
          (p) =>
            p.relatedProduct._id === cartProduct.productID &&
            p.receptionReferenceId === null
        );
        if (edbProduct) {
          tmpProducts.push({
            _id: edbProduct.relatedProduct._id,
            qty: edbProduct.qty + cartProduct.qty,
            receptionReferenceId: null,
          });
        } else {
          tmpProducts.push({
            _id: cartProduct.productID,
            qty: cartProduct.qty,
            receptionReferenceId: null,
          });
        }
      });
      try {
        await EDBService.UpdateEDBProducts(
          token,
          { newProducts: tmpProducts, integratedOrders: data.integratedOrders },
          edb._id
        );
        setSubmiting(false);
        setErrorForm("");
      } catch (error) {
        setErrorForm(
          "Une erreur est intervenue lors de la modification des produits de l'EDB"
        );
        setSubmiting(false);
        throw error;
      }
    },
    [token]
  );

  const generatePDF = useCallback(
    async (opId: string, op: any) => {
      await EDBService.ExportPdf(token, opId, op).then((response) =>
        FileDownload(response.data, `${op?.code}.pdf`)
      );
    },
    [token]
  );

  return {
    step,
    setStep,
    deleteEDB,
    saveEDB,
    updateEDB,
    updateEDBProducts,
    errorFrom,
    submiting,
    isModifyingDelivery,
    setIsModifyingDelivery,
    generatePDF,
  };
};

export default useEDB;
